import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Lock } from "lucide-react";
import { LOGO } from "./assets";

const LINKS = [
  { label: "Results", href: "#results" },
  { label: "Services", href: "#services" },
  { label: "Train Anywhere", href: "#train-anywhere" },
  { label: "Shop", href: "#shop" },
  { label: "Forms", href: "#forms" },
  { label: "Contact", href: "#contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const scrollTo = (href) => {
    setOpen(false);
    setTimeout(() => {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }, 60);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/70 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center group"
        >
          <img src={LOGO} alt="JJ Speed Factory" className="h-11 md:h-12 w-auto object-contain" />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              onClick={() => scrollTo(l.href)}
              className="font-mono-accent text-xs tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <a
          href="/online-coaching"
          data-testid="nav-train-online-link"
          className="hidden lg:inline-flex items-center font-mono-accent text-xs tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors duration-300 mr-4"
        >
          Online Coaching
        </a>

        <button
          data-testid="nav-book-btn"
          onClick={() => scrollTo("#assessment")}
          className="hidden lg:inline-flex bg-white text-black uppercase tracking-[0.2em] font-bold text-xs px-6 py-3 hover:bg-white/85 transition-colors duration-300"
        >
          Book Free Evaluation
        </button>

        <a
          href="/login"
          data-testid="nav-login-link"
          className="hidden lg:inline-flex items-center gap-2 font-mono-accent text-xs tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors duration-300 ml-2"
        >
          <Lock size={13} /> Login
        </a>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-white"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-b border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4 max-h-[calc(100vh-5rem)] overflow-y-auto">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.href.replace("#", "")}`}
                  onClick={() => scrollTo(l.href)}
                  className="font-display uppercase text-2xl text-left tracking-tight text-white/90 hover:text-white transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <a
                href="/online-coaching"
                data-testid="nav-mobile-train-online-link"
                className="font-mono-accent text-sm tracking-[0.2em] uppercase text-white/70 hover:text-white transition-colors"
              >
                Online Coaching
              </a>
              <button
                data-testid="nav-mobile-book-btn"
                onClick={() => scrollTo("#assessment")}
                className="bg-white text-black uppercase tracking-[0.2em] font-bold text-sm px-6 py-4 mt-2"
              >
                Book Free Evaluation
              </button>
              <a
                href="/login"
                data-testid="nav-mobile-login-link"
                className="inline-flex items-center gap-2 font-mono-accent text-sm tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors mt-1"
              >
                <Lock size={15} /> Portal Login
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
